import { VARIATION_WEIGHT } from "../constants/VariationConstant";

/**
 * Article codes: the one identifier the order picking system uses for "this physical thing".
 *
 *   {shopSource}-{productId}              a product sold without weight variants
 *   {shopSource}-{productId}-w{grams}     one weight of a product, e.g. VCE-a1b2c3-w250
 *
 * Ported from the Picqer integration (api, branch picqer-koppeling, PicqerProductMapper), where
 * the reasoning is recorded in planning/picqer-productmapping.md:
 *
 *  - Weight, not variantId, tells variants apart, because a package component only carries a
 *    weight (`productItems[].value`) and never a variantId. Keying on variantId would make
 *    package contents unmappable.
 *  - The shop is part of the code because VCE and HVO keep their own product ids - and, since
 *    HVO started as a copy of VCE, the same id may well exist in both.
 *
 * The code is never stored on a product or a pick route entry. It is a pure function of the pick
 * line, so it cannot drift when the legacy `cms/` rewrites a product document, and the wave
 * document freezes it at the moment a round is composed.
 *
 * Pure: no Firestore, no React.
 */

/** Identity of a product across both shops. The same key the pick route and product data use. */
export const productKey = (shopSource, productId) => `${shopSource}|${productId}`;

/** A package ("barbecuepakket") is never picked itself; its components are. */
export const isPackageProduct = (product) => Array.isArray(product?.productItems) && product.productItems.length > 0;

const DECIMAL = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;

/** 250.0 becomes "250"; 12.5 stays "12.5". */
export const trimTrailingZero = (value) => {
  if (!Number.isFinite(value)) return String(value);
  return Number.isInteger(value) ? String(Math.trunc(value)) : String(value);
};

/**
 * One spelling per weight, so "250", "250.0", "250,0" and 250 land on the same article.
 *
 * Returns null for "no weight": missing, empty, or zero. That is one deliberate difference from
 * the Java original, which only treated the literal string "0" as no weight and turned "0.0" into
 * a `-w0` article nobody sells. A value that is not a number at all ("2x125") is kept verbatim,
 * as the Java code did: it keeps the code stable and traceable instead of silently merging two
 * variants into one.
 */
export const normaliseWeight = (weight) => {
  if (weight === null || weight === undefined) return null;
  if (typeof weight === "number") {
    if (!Number.isFinite(weight) || weight === 0) return null;
    return trimTrailingZero(weight);
  }
  const trimmed = String(weight).trim();
  if (trimmed === "") return null;
  const candidate = trimmed.replace(/,/g, ".");
  if (!DECIMAL.test(candidate)) return trimmed;
  const parsed = Number(candidate);
  if (parsed === 0) return null;
  return trimTrailingZero(parsed);
};

/** The article code for one product, optionally one weight of it. */
export const articleCode = (shopSource, productId, weight) => {
  const normalised = normaliseWeight(weight);
  const base = `${shopSource}-${productId}`;
  return normalised === null ? base : `${base}-w${normalised}`;
};

/** The code without any weight: what a product sold without variants is picked as. */
export const articleBaseCode = (shopSource, productId) => `${shopSource}-${productId}`;

const ARTICLE_CODE = /^(VCE|HVO)-(.+?)(?:-w(\d+(?:\.\d+)?))?$/;

/**
 * The reverse, for reading a code back (exceptions, scans, debugging). Only numeric weights are
 * recognised as a weight part, so a product id that itself contains "-w" (a slug like
 * "kip-wings") is not mistaken for a weight variant.
 */
export const parseArticleCode = (code) => {
  const match = ARTICLE_CODE.exec(code || "");
  if (!match) return null;
  return { shopSource: match[1], productId: match[2], weight: match[3] === undefined ? null : match[3] };
};

/** "Biefstuk (250 gram)" - the weight belongs in the name so the picker sees what to grab. */
export const displayName = (name, weight) => {
  const normalised = normaliseWeight(weight);
  return normalised === null ? name : `${name} (${normalised} gram)`;
};

/** "250 gram", or null for no weight. What a wave line shows under the product name. */
export const weightLabel = (weight) => {
  const normalised = normaliseWeight(weight);
  return normalised === null ? null : `${normalised} gram`;
};

/** Numeric weights ascending, anything non-numeric after them, so a product's vakken read in order. */
export const compareWeights = (a, b) => {
  const left = Number(a);
  const right = Number(b);
  const leftNumeric = a !== null && Number.isFinite(left);
  const rightNumeric = b !== null && Number.isFinite(right);
  if (leftNumeric && rightNumeric) return left - right;
  if (leftNumeric) return -1;
  if (rightNumeric) return 1;
  return String(a || "").localeCompare(String(b || ""), "nl");
};

/**
 * Every article in the catalogue, per product: what can end up on a pick line, and therefore
 * what needs a vak.
 *
 *  - one article per weight in the product's `variations[]`;
 *  - one per weight that only a package asks for ("2 × gehakt 500 g" while gehakt is sold loose
 *    per 250 g) - the Picqer mapping learned the hard way that forgetting these makes every order
 *    with that package fail on an unknown article;
 *  - the base article when the product has no weight at all.
 *
 * Packages and gift cards have no articles of their own. Returns
 * Map<productKey, { key, shopSource, productId, name, articles: [{ articleCode, weight, sources }] }>.
 */
export const buildArticleCatalogue = (products) => {
  const productsByKey = new Map();
  (products || []).forEach((product) => {
    if (!!product && !!product.id && !!product.shopSource) {
      productsByKey.set(productKey(product.shopSource, product.id), product);
    }
  });

  const catalogue = new Map();
  const ensure = (product) => {
    const key = productKey(product.shopSource, product.id);
    if (!catalogue.has(key)) {
      catalogue.set(key, {
        key,
        shopSource: product.shopSource,
        productId: product.id,
        name: product.name || "",
        weights: new Map(),
      });
    }
    return catalogue.get(key);
  };
  const addWeight = (entry, weight, source) => {
    const sources = entry.weights.get(weight) || new Set();
    sources.add(source);
    entry.weights.set(weight, sources);
  };

  productsByKey.forEach((product) => {
    if (isPackageProduct(product) || product.giftCard) return;
    const entry = ensure(product);
    (product.variations || []).forEach((variation) => {
      const weight = normaliseWeight(variation?.weight);
      if (weight !== null) addWeight(entry, weight, "variation");
    });
  });

  productsByKey.forEach((product) => {
    if (!isPackageProduct(product)) return;
    product.productItems.forEach((item) => {
      if (!item || !item.productId || item.type !== VARIATION_WEIGHT) return;
      const component = productsByKey.get(productKey(product.shopSource, item.productId));
      if (!component || isPackageProduct(component) || component.giftCard) return;
      const weight = normaliseWeight(item.value);
      if (weight !== null) addWeight(ensure(component), weight, "package");
    });
  });

  const result = new Map();
  catalogue.forEach((entry, key) => {
    const weights = [...entry.weights.keys()].sort(compareWeights);
    const articles =
      weights.length === 0
        ? [{ articleCode: articleBaseCode(entry.shopSource, entry.productId), weight: null, sources: ["base"] }]
        : weights.map((weight) => ({
            articleCode: articleCode(entry.shopSource, entry.productId, weight),
            weight,
            sources: [...entry.weights.get(weight)].sort(),
          }));
    result.set(key, { key, shopSource: entry.shopSource, productId: entry.productId, name: entry.name, articles });
  });
  return result;
};

/** The articles of one product, or [] when it has none (a package, a gift card, unknown). */
export const articlesForProduct = (catalogue, shopSource, productId) =>
  (catalogue && catalogue.get(productKey(shopSource, productId))?.articles) || [];
